import React, { useState, useEffect, useRef, useMemo } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import {
  TwitterShareButton,
  FacebookShareButton,
  EmailShareButton,
  RedditShareButton,
  LinkedinShareButton,
  XIcon,
  FacebookIcon,
  EmailIcon,
  RedditIcon,
  LinkedinIcon
} from 'react-share';
import Header from '../components/Header';
import Footer from '../components/Footer';
import ChapterNavigation from '../components/ChapterNavigation';
import ComingSoon from '../components/ComingSoon';
import { AdIcons } from '../components/Ad';

const COMMENTARY_MAP = {
  genesis: {
    1: 'MHC01001.HTM',
    2: 'MHC01002.HTM',
    3: 'MHC01003.HTM',
    4: 'MHC01004.HTM',
    5: 'MHC01005.HTM',
    6: 'MHC01006.HTM',
    7: 'MHC01007.HTM',
    8: 'MHC01008.HTM',
    9: 'MHC01009.HTM',
    10: 'MHC01010.HTM',
    11: 'MHC01011.HTM',
    12: 'MHC01012.HTM',
    13: 'MHC01013.HTM',
    14: 'MHC01014.HTM',
    15: 'MHC01015.HTM',
    16: 'MHC01016.HTM',
    17: 'MHC01017.HTM',
    18: 'MHC01018.HTM',
    19: 'MHC01019.HTM',
    20: 'MHC01020.HTM',
    21: 'MHC01021.HTM',
    22: 'MHC01022.HTM',
    23: 'MHC01023.HTM',
    24: 'MHC01024.HTM',
    25: 'MHC01025.HTM',
    26: 'MHC01026.HTM',
    27: 'MHC01027.HTM',
    28: 'MHC01028.HTM',
    29: 'MHC01029.HTM',
    30: 'MHC01030.HTM',
    31: 'MHC01031.HTM',
    32: 'MHC01032.HTM',
    33: 'MHC01033.HTM',
    34: 'MHC01034.HTM',
    35: 'MHC01035.HTM',
    36: 'MHC01036.HTM',
    37: 'MHC01037.HTM',
    38: 'MHC01038.HTM',
    39: 'MHC01039.HTM',
    40: 'MHC01040.HTM',
    41: 'MHC01041.HTM',
    42: 'MHC01042.HTM',
    43: 'MHC01043.HTM',
    44: 'MHC01044.HTM',
    45: 'MHC01045.HTM',
    46: 'MHC01046.HTM',
    47: 'MHC01047.HTM',
    48: 'MHC01048.HTM',
    49: 'MHC01049.HTM',
    50: 'MHC01050.HTM',
  },
};

const COMMENTARY_SUPPORTED_TYPES = ['norm', 'ruby', 'gal'];

const prepareCommentaryHtml = (html) => {
  const parser = new DOMParser();
  const doc = parser.parseFromString(html, 'text/html');
  const body = doc.body;

  if (!body) {
    return html;
  }

  body.querySelectorAll('table').forEach((table) => {
    const linksToCommentaryFiles = Array.from(table.querySelectorAll('a'))
      .some((link) => /^MHC\d+\.HTM$/i.test(link.getAttribute('href') || ''));

    if (linksToCommentaryFiles) {
      table.remove();
    }
  });

  body.querySelectorAll('body > hr').forEach((hr) => hr.remove());
  body.querySelectorAll('center').forEach((center) => {
    center.classList.add('commentary-chapter-heading');
  });
  body.querySelectorAll('table').forEach((table) => {
    table.classList.add('commentary-section-heading');
  });

  return body.innerHTML;
};

const findVerseIdFromTarget = (target) => {
  const verseMarker = target.closest('em[id]');
  if (verseMarker) {
    return verseMarker.id;
  }

  let verseText = target.closest('span[id="verse"]');
  while (verseText) {
    const previous = verseText.previousElementSibling;
    if (previous && previous.matches('em[id]')) {
      return previous.id;
    }
    verseText = verseText.parentElement?.closest('span[id="verse"]');
  }

  return '';
};

// Custom Blogger Share Button
const BloggerShareButton = ({ url, title, text, image, children }) => {
  // Clean text: remove HTML tags and markdown syntax
  let cleanText = text || '';

  // Remove HTML tags
  cleanText = cleanText.replace(/<[^>]*>/g, '');

  // Remove markdown image syntax
  cleanText = cleanText.replace(/!\[.*?\]\(.*?\)/g, '');

  // Remove markdown headers
  cleanText = cleanText.replace(/^#{1,6}\s+/gm, '');

  // Remove markdown bold/italic
  cleanText = cleanText.replace(/[*_]{1,2}([^*_]+)[*_]{1,2}/g, '$1');

  // Remove markdown links but keep text
  cleanText = cleanText.replace(/\[([^\]]+)\]\([^)]+\)/g, '$1');

  // Normalize whitespace
  cleanText = cleanText.replace(/\s+/g, ' ').trim();

  // Truncate to 1000 chars to avoid URL length issues
  const truncatedText = cleanText.length > 1000 ? cleanText.substring(0, 997) + '...' : cleanText;

  // Build HTML content: image + text + link at bottom
  const imageTag = image ? `<img src="${image}" alt="${title}" style="max-width:100%;height:auto;"><br><br>` : '';
  const content = `${imageTag}${truncatedText}<br><br><a href="${url}">${url}</a>`;

  const bloggerUrl = `https://www.blogger.com/blog-this.g?u=${encodeURIComponent(url)}&n=${encodeURIComponent(title)}&t=${encodeURIComponent(content)}`;

  return (
    <a
      href={bloggerUrl}
      target="_blank"
      rel="noopener noreferrer"
      style={{ display: 'inline-block', cursor: 'pointer' }}
    >
      {children}
    </a>
  );
};

// Custom Blogger Icon
const BloggerIcon = ({ size = 32 }) => (
  <svg
    viewBox="0 0 24 24"
    width={size}
    height={size}
    style={{ display: 'block' }}
  >
    <rect width="24" height="24" fill="#FF5722" />
    <path
      d="M10.5 9h3c.28 0 .5.22.5.5s-.22.5-.5.5h-3c-.28 0-.5-.22-.5-.5s.22-.5.5-.5zm0 5h3c.28 0 .5.22.5.5s-.22.5-.5.5h-3c-.28 0-.5-.22-.5-.5s.22-.5.5-.5zm6.5-7h-2.5V5c0-1.1-.9-2-2-2h-4C7.9 3 7 3.9 7 5v4c0 1.1.9 2 2 2v1c0 1.1.9 2 2 2v3c0 1.1.9 2 2 2h4c1.1 0 2-.9 2-2V9c0-1.1-.9-2-2-2z"
      fill="white"
    />
  </svg>
);

function Bible() {
  const { section, chapter, verse } = useParams();
  const [searchParams, setSearchParams] = useSearchParams();
  const [content, setContent] = useState('');
  const [currentChapter, setCurrentChapter] = useState('');
  const [showComingSoon, setShowComingSoon] = useState(false);
  const [metaData, setMetaData] = useState({
    title: '',
    description: '',
    url: '',
    image: ''
  });
  const [sharePopup, setSharePopup] = useState({
    show: false,
    title: '',
    text: '',
    url: '',
    x: 0,
    y: 0,
    preview: '' // For showing selected verses
  });
  const [hasScrolledToVerse, setHasScrolledToVerse] = useState(false);
  const [commentaryHtml, setCommentaryHtml] = useState('');
  const [commentaryAnchor, setCommentaryAnchor] = useState('');
  const [selectedCommentaryReference, setSelectedCommentaryReference] = useState('');
  const [hasManualCommentarySelection, setHasManualCommentarySelection] = useState(false);
  const [isCommentaryLoading, setIsCommentaryLoading] = useState(false);
  const [isCommentaryPanelOpen, setIsCommentaryPanelOpen] = useState(false);
  const commentaryContainerRef = useRef(null);

  // Reset scroll flag when URL changes
  useEffect(() => {
    setHasScrolledToVerse(false);
  }, [section, chapter, verse]);

  useEffect(() => {
    setHasManualCommentarySelection(false);
  }, [section, chapter, verse]);

  const sectionMap = useMemo(() => ({
    psalms: '3869678226',
    john: '3493586185',
    nahum: '2341997426',
    joel: '1554179965',
    haggai: '2264983860',
    luke: '3571744779',
    philippians: '160214243',
    titus: '4084456418',
    jonah: '3140582404',
    matthew: '3929365192',
    songofsongs: '1335264384',
    ecclesiastes: '3906097047',
    '2samuel': '4072766808',
    lamentations: '1475919099',
    leviticus: '2606473253',
    mark: '2251790182',
    zephaniah: '3193855292',
    numbers: '777940117',
    genesis: '1234567890',
    exodus: '9876543210',
    // Deuterocanonical books
    tobit: '3869678226',
    judith: '3869678226',
    wisdom: '3869678226',
    sirach: '3869678226',
    baruch: '3869678226',
    '1maccabees': '3869678226',
    '2maccabees': '3869678226',
    susanna: '3869678226',
    'prayer-of-manasseh': '3869678226',
  }), []);

  // Japanese book names mapping
  const bookNamesJa = useMemo(() => ({
    genesis: '創世記',
    exodus: '出エジプト記',
    leviticus: 'レビ記',
    numbers: '民数記',
    deuteronomy: '申命記',
    joshua: 'ヨシュア記',
    judges: '士師記',
    ruth: 'ルツ記',
    '1samuel': 'サムエル記上',
    '2samuel': 'サムエル記下',
    '1kings': '列王記上',
    '2kings': '列王記下',
    '1chronicles': '歴代志上',
    '2chronicles': '歴代志下',
    ezra: 'エズラ記',
    nehemiah: 'ネヘミヤ記',
    esther: 'エステル記',
    job: 'ヨブ記',
    psalms: '詩篇',
    proverbs: '箴言',
    ecclesiastes: '伝道の書',
    songofsongs: '雅歌',
    isaiah: 'イザヤ書',
    jeremiah: 'エレミヤ書',
    lamentations: '哀歌',
    ezekiel: 'エゼキエル書',
    daniel: 'ダニエル書',
    hosea: 'ホセア書',
    joel: 'ヨエル書',
    amos: 'アモス書',
    obadiah: 'オバデヤ書',
    jonah: 'ヨナ書',
    micah: 'ミカ書',
    nahum: 'ナホム書',
    habakkuk: 'ハバクク書',
    zephaniah: 'ゼパニヤ書',
    haggai: 'ハガイ書',
    zechariah: 'ゼカリヤ書',
    malachi: 'マラキ書',
    matthew: 'マタイによる福音書',
    mark: 'マルコによる福音書',
    luke: 'ルカによる福音書',
    john: 'ヨハネによる福音書',
    acts: '使徒行伝',
    romans: 'ローマ人への手紙',
    '1corinthians': 'コリント人への第一の手紙',
    '2corinthians': 'コリント人への第二の手紙',
    galatians: 'ガラテヤ人への手紙',
    ephesians: 'エペソ人への手紙',
    philippians: 'ピリピ人への手紙',
    colossians: 'コロサイ人への手紙',
    '1thessalonians': 'テサロニケ人への第一の手紙',
    '2thessalonians': 'テサロニケ人への第二の手紙',
    '1timothy': 'テモテへの第一の手紙',
    '2timothy': 'テモテへの第二の手紙',
    titus: 'テトスへの手紙',
    philemon: 'ピレモンへの手紙',
    hebrews: 'ヘブル人への手紙',
    james: 'ヤコブの手紙',
    '1peter': 'ペテロの第一の手紙',
    '2peter': 'ペテロの第二の手紙',
    '1john': 'ヨハネの第一の手紙',
    '2john': 'ヨハネの第二の手紙',
    '3john': 'ヨハネの第三の手紙',
    jude: 'ユダの手紙',
    revelation: 'ヨハネの黙示録',
    // Deuterocanonical books
    tobit: 'トビト記',
    judith: 'ユディト記',
    wisdom: '知恵の書',
    sirach: 'シラ書',
    baruch: 'バルク書',
    'letter-of-jeremiah': 'エレミヤの書翰',
    'song-of-three': '三童児の歌',
    '1maccabees': 'マカビー第一書',
    '2maccabees': 'マカバイ記二',
    susanna: 'スザンナ',
    'bel-and-dragon': 'ベルと龍',
    'prayer-of-manasseh': 'マナセの祈禱',
  }), []);

  useEffect(() => {
    if (chapter) {
      setCurrentChapter(chapter);
    } else {
      setCurrentChapter('1');
    }
  }, [section, chapter]);

  const album = sectionMap[section] || '3869678226';
  const typeParam = searchParams.get('type');
  const allowedTypes = useMemo(() => (
    section === 'genesis'
      ? ['norm', 'ruby', 'en', 'gal']
      : ['norm', 'ruby', 'en']
  ), [section]);
  const currentType = allowedTypes.includes(typeParam) ? typeParam : 'norm';
  const supportsCommentary = COMMENTARY_SUPPORTED_TYPES.includes(currentType) && Boolean(COMMENTARY_MAP[section]);

  const effectiveChapter = useMemo(() => {
    const routeChapterNumber = chapter ? parseInt(chapter, 10) : NaN;
    if (!Number.isNaN(routeChapterNumber)) {
      return routeChapterNumber;
    }
    const stateChapterNumber = currentChapter ? parseInt(currentChapter, 10) : NaN;
    if (!Number.isNaN(stateChapterNumber)) {
      return stateChapterNumber;
    }
    return 1;
  }, [chapter, currentChapter]);

  useEffect(() => {
    if (!supportsCommentary) {
      setHasManualCommentarySelection(false);
      setIsCommentaryPanelOpen(false);
    }
  }, [supportsCommentary]);

  useEffect(() => {
    let type = searchParams.get('type');

    // For all books, use existing logic to validate type
    if (!allowedTypes.includes(type)) {
      type = 'norm';
      setSearchParams({ type });
    }

    setShowComingSoon(false);
    fetch(`${process.env.PUBLIC_URL}/static/html/${type}/${section}.htm`)
      .then(response => {
        if (!response.ok) {
          throw new Error(`HTTP ${response.status}`);
        }
        return response.text();
      })
      .then(data => setContent(data))
      .catch(error => {
        console.error('Error loading the HTML content:', error);
        setShowComingSoon(true);
        setContent('');
      });
  }, [section, searchParams, setSearchParams, allowedTypes]);

  useEffect(() => {
    if (!supportsCommentary) {
      setCommentaryHtml('');
      setCommentaryAnchor('');
      setSelectedCommentaryReference('');
      setIsCommentaryLoading(false);
      return;
    }

    const commentaryFile = COMMENTARY_MAP[section]?.[effectiveChapter];

    if (!commentaryFile) {
      setCommentaryHtml('<p class="commentary-placeholder">この章の註解は準備中です。</p>');
      setCommentaryAnchor('');
      setSelectedCommentaryReference('');
      setIsCommentaryLoading(false);
      return;
    }

    setIsCommentaryLoading(true);
    const commentaryPaths = [
      `${process.env.PUBLIC_URL}/commentary/ja/${commentaryFile}`,
      `${process.env.PUBLIC_URL}/commentary/en/${commentaryFile}`,
    ];
    let isMounted = true;

    const fetchFirstAvailableCommentary = async () => {
      let lastError = null;

      for (const commentaryPath of commentaryPaths) {
        try {
          const response = await fetch(commentaryPath);
          if (!response.ok) {
            throw new Error(`HTTP ${response.status}`);
          }
          return response.text();
        } catch (error) {
          lastError = error;
        }
      }

      throw lastError || new Error('Commentary not found');
    };

    fetchFirstAvailableCommentary()
      .then((data) => {
        if (!isMounted) {
          return;
        }
        setCommentaryHtml(prepareCommentaryHtml(data));
        setIsCommentaryLoading(false);
      })
      .catch((error) => {
        console.error('Error loading the commentary content:', error);
        if (isMounted) {
          setCommentaryHtml('<p class="commentary-placeholder">註解の読み込みに失敗しました。</p>');
          setIsCommentaryLoading(false);
        }
      });

    return () => {
      isMounted = false;
    };
  }, [supportsCommentary, section, effectiveChapter, currentType]);

  useEffect(() => {
    if (!supportsCommentary) {
      return;
    }

    const commentaryFile = COMMENTARY_MAP[section]?.[effectiveChapter];

    if (!commentaryFile) {
      setCommentaryAnchor('');
      setSelectedCommentaryReference('');
      return;
    }

    if (verse) {
      const verseNumber = parseInt(verse, 10);
      if (!Number.isNaN(verseNumber)) {
        setCommentaryAnchor(`Ge${effectiveChapter}_${verseNumber}`);
        setSelectedCommentaryReference(`${effectiveChapter}:${verseNumber}`);
      }
      return;
    }

    if (!hasManualCommentarySelection) {
      setCommentaryAnchor(`Ge${effectiveChapter}_1`);
      setSelectedCommentaryReference(`${effectiveChapter}:1`);
    }
  }, [supportsCommentary, section, verse, hasManualCommentarySelection, effectiveChapter]);

  useEffect(() => {
    document.querySelectorAll('.verse-commentary-active').forEach((element) => {
      element.classList.remove('verse-commentary-active');
    });

    if (!commentaryAnchor) {
      return;
    }

    const match = commentaryAnchor.match(/^Ge(\d+)_(\d+)$/);
    if (!match) {
      return;
    }

    const verseId = `${match[1]}-${match[2]}`;
    const marker = document.getElementById(verseId);
    marker?.classList.add('verse-commentary-active');
    marker?.nextElementSibling?.classList.add('verse-commentary-active');
  }, [commentaryAnchor, content]);

  useEffect(() => {
    const container = commentaryContainerRef.current;
    if (!container) {
      return;
    }

    container.querySelectorAll('.commentary-target-highlight').forEach((element) => {
      element.classList.remove('commentary-target-highlight');
    });

    if (!commentaryAnchor) {
      container.scrollTop = 0;
      return;
    }

    const anchorElement = container.querySelector(`[name="${commentaryAnchor}"]`);
    if (anchorElement) {
      let node = anchorElement.nextSibling;
      let hasHighlightedContent = false;
      let firstHighlightedElement = null;

      while (node) {
        if (node.nodeType === Node.ELEMENT_NODE) {
          const element = node;
          const name = element.getAttribute('name');

          if (hasHighlightedContent && element.tagName === 'A' && name && /^Ge\d+_\d+$/.test(name)) {
            break;
          }

          const text = element.textContent.replace(/\s+/g, ' ').trim();
          if (text) {
            element.classList.add('commentary-target-highlight');
            firstHighlightedElement = firstHighlightedElement || element;
            hasHighlightedContent = true;
          }
        }

        node = node.nextSibling;
      }

      const scrollTarget = firstHighlightedElement || anchorElement;
      const containerRect = container.getBoundingClientRect();
      const targetRect = scrollTarget.getBoundingClientRect();
      const offset = targetRect.top - containerRect.top - 18;
      const nextScrollTop = container.scrollTop + offset;
      container.scrollTop = nextScrollTop < 0 ? 0 : nextScrollTop;
    }
  }, [commentaryHtml, commentaryAnchor]);

  const handleChapterSelect = (chapterId) => {
    setCurrentChapter(chapterId);

    if (!supportsCommentary) {
      return;
    }

    const numericChapter = parseInt(chapterId, 10);
    if (Number.isNaN(numericChapter)) {
      setCommentaryAnchor('');
      setSelectedCommentaryReference('');
      return;
    }

    const commentaryFile = COMMENTARY_MAP[section]?.[numericChapter];
    if (!commentaryFile) {
      setCommentaryAnchor('');
      setSelectedCommentaryReference('');
      return;
    }

    setHasManualCommentarySelection(false);
    setCommentaryAnchor(`Ge${numericChapter}_1`);
    setSelectedCommentaryReference(`${numericChapter}:1`);
  };

  const handleCommentaryChapterChange = (direction) => {
    if (!supportsCommentary) {
      return;
    }

    const nextChapter = effectiveChapter + direction;
    const commentaryFile = COMMENTARY_MAP[section]?.[nextChapter];
    if (!commentaryFile) {
      return;
    }

    setCurrentChapter(String(nextChapter));
    setCommentaryAnchor(`Ge${nextChapter}_1`);
    setSelectedCommentaryReference(`${nextChapter}:1`);
    setHasManualCommentarySelection(false);
  };

  const hasPreviousCommentaryChapter = Boolean(COMMENTARY_MAP[section]?.[effectiveChapter - 1]);
  const hasNextCommentaryChapter = Boolean(COMMENTARY_MAP[section]?.[effectiveChapter + 1]);

  // Update metadata based on URL parameters (for specific verse)
  useEffect(() => {
    if (!content) return;

    // Remove previous highlights
    const previousHighlights = document.querySelectorAll('.verse-highlight');
    previousHighlights.forEach(el => el.classList.remove('verse-highlight'));

    if (chapter && verse) {
      // URL has chapter and verse like /bible/genesis/1/1 or /bible/genesis/1/1?end=3
      const endVerse = searchParams.get('end');
      const startVerseNum = parseInt(verse);
      const endVerseNum = endVerse ? parseInt(endVerse) : startVerseNum;

      // Collect all verse texts for metadata
      let allVerseTexts = [];

      // Highlight all verses in range
      for (let v = startVerseNum; v <= endVerseNum; v++) {
        const verseId = `${chapter}-${v}`;
        const verseElement = document.getElementById(verseId);

        if (verseElement) {
          const nextElement = verseElement.nextElementSibling;

          // Highlight the verse
          verseElement.classList.add('verse-highlight');
          if (nextElement) {
            nextElement.classList.add('verse-highlight');

            // Collect verse text
            const verseSpans = nextElement.querySelectorAll('span[id="verse"]');
            const verseText = verseSpans.length > 0
              ? verseSpans[verseSpans.length - 1].textContent.trim()
              : nextElement.textContent.trim();
            allVerseTexts.push(verseText);
          }

          // Scroll to the first verse only once when first loaded
          if (v === startVerseNum && !hasScrolledToVerse) {
            setTimeout(() => {
              const yOffset = -150; // Offset to center verse better
              const y = verseElement.getBoundingClientRect().top + window.pageYOffset + yOffset;
              window.scrollTo({ top: y, behavior: 'smooth' });
            }, 100);
            setHasScrolledToVerse(true);
          }
        }
      }

      // Set metadata
      const bookNameJa = bookNamesJa[section] || section;
      const verseRef = endVerse
        ? `${bookNameJa} ${chapter}:${verse}-${endVerse}`
        : `${bookNameJa} ${chapter}:${verse}`;
      const combinedText = allVerseTexts.join(' ');

      setMetaData({
        title: verseRef,
        description: combinedText || '口語訳聖書 旧約：1955年版・新約：1954年版',
        url: `https://www.asmrchurch.com/bible/${section}/${chapter}/${verse}${endVerse ? `?end=${endVerse}` : ''}`,
        image: 'https://www.asmrchurch.com/static/images/card1.jpg'
      });

      return;
    }

    // Default metadata for the book
    const bookNameJa = bookNamesJa[section] || section;
    setMetaData({
      title: `${bookNameJa} - ASMRキリスト教会`,
      description: '口語訳聖書 旧約：1955年版・新約：1954年版',
      url: `https://www.asmrchurch.com/bible/${section}`,
      image: 'https://www.asmrchurch.com/static/images/card1.jpg'
    });
  }, [content, section, chapter, verse, searchParams, bookNamesJa, hasScrolledToVerse]);

  // Add click handlers for verse sharing
  useEffect(() => {
    if (!content) return;

    const handleVerseClick = (e) => {
      // Check if clicked element is a verse link
      const link = e.target.closest('em[id] a[href^="#"]');
      if (!link) return;

      e.preventDefault();
      const verseId = link.parentElement.id; // e.g., "1-1"
      const [chapterNum, verseNum] = verseId.split('-');

      // Get the verse text
      const verseElement = link.parentElement.nextElementSibling;
      if (!verseElement) return;

      // Get the innermost span text (nested spans with id='verse')
      const verseSpans = verseElement.querySelectorAll('span[id="verse"]');
      const verseText = verseSpans.length > 0
        ? verseSpans[verseSpans.length - 1].textContent.trim()
        : verseElement.textContent.trim();

      // Get book name in Japanese
      const bookNameJa = bookNamesJa[section] || section;

      // Construct share text with new URL format
      const shareTitle = `${bookNameJa} ${chapterNum}:${verseNum}`;
      const shareText = `${verseText} - ${shareTitle}`;
      const shareUrl = `${window.location.origin}/bible/${section}/${chapterNum}/${verseNum}`;

      // Get click position for popup
      const rect = link.getBoundingClientRect();

      // Show share popup with X and Facebook buttons
      setSharePopup({
        show: true,
        title: shareTitle,
        text: shareText,
        url: shareUrl,
        x: rect.left + window.scrollX,
        y: rect.bottom + window.scrollY + 5,
        preview: '' // No preview for single verse click
      });

      if (supportsCommentary) {
        const chapterNumber = parseInt(chapterNum, 10);
        const verseNumber = parseInt(verseNum, 10);
        const commentaryFile = COMMENTARY_MAP[section]?.[chapterNumber];

        if (!Number.isNaN(chapterNumber) && !Number.isNaN(verseNumber) && commentaryFile) {
          setCommentaryAnchor(`Ge${chapterNumber}_${verseNumber}`);
          setSelectedCommentaryReference(`${chapterNumber}:${verseNumber}`);
          setHasManualCommentarySelection(true);
        }
      }
    };

    const selectCommentaryForVerse = (verseId) => {
      if (!supportsCommentary || !verseId) {
        return false;
      }

      const [chapterNum, verseNum] = verseId.split('-');
      const chapterNumber = parseInt(chapterNum, 10);
      const verseNumber = parseInt(verseNum, 10);
      const commentaryFile = COMMENTARY_MAP[section]?.[chapterNumber];

      if (Number.isNaN(chapterNumber) || Number.isNaN(verseNumber) || !commentaryFile) {
        return false;
      }

      const nextAnchor = `Ge${chapterNumber}_${verseNumber}`;

      setCurrentChapter(chapterNum);
      setCommentaryAnchor(nextAnchor);
      setSelectedCommentaryReference(`${chapterNumber}:${verseNumber}`);
      setHasManualCommentarySelection(true);
      setIsCommentaryPanelOpen(true);
      setSharePopup((current) => ({ ...current, show: false }));

      return true;
    };

    // Handle right-click on selected verses
    const handleContextMenu = (e) => {
      const selection = window.getSelection();
      if (!selection || selection.isCollapsed) {
        const verseId = findVerseIdFromTarget(e.target);
        if (selectCommentaryForVerse(verseId)) {
          e.preventDefault();
        }
        return;
      }

      const selectedText = selection.toString().trim();
      if (!selectedText) return;

      // Find all verse number elements (em tags with id) in the selected area
      const allEmElements = document.querySelectorAll('em[id]');
      const selectedVerses = [];

      allEmElements.forEach(emElement => {
        if (selection.containsNode(emElement, true) ||
            selection.containsNode(emElement.nextElementSibling, true)) {
          const verseId = emElement.id;
          const [chap, ver] = verseId.split('-');
          const verseTextElement = emElement.nextElementSibling;

          if (verseTextElement) {
            const verseSpans = verseTextElement.querySelectorAll('span[id="verse"]');
            const verseText = verseSpans.length > 0
              ? verseSpans[verseSpans.length - 1].textContent.trim()
              : verseTextElement.textContent.trim();

            selectedVerses.push({
              chapter: chap,
              verse: ver,
              text: verseText
            });
          }
        }
      });

      if (selectedVerses.length > 0) {
        e.preventDefault();

        // Get book name in Japanese
        const bookNameJa = bookNamesJa[section] || section;

        // Construct combined text
        const firstVerse = selectedVerses[0];
        const lastVerse = selectedVerses[selectedVerses.length - 1];

        let shareTitle, shareUrl;
        if (selectedVerses.length === 1) {
          shareTitle = `${bookNameJa} ${firstVerse.chapter}:${firstVerse.verse}`;
          shareUrl = `${window.location.origin}/bible/${section}/${firstVerse.chapter}/${firstVerse.verse}`;
        } else {
          shareTitle = `${bookNameJa} ${firstVerse.chapter}:${firstVerse.verse}-${lastVerse.verse}`;
          shareUrl = `${window.location.origin}/bible/${section}/${firstVerse.chapter}/${firstVerse.verse}?end=${lastVerse.verse}`;
        }

        // Combine all verse texts
        const combinedText = selectedVerses.map((v, i) =>
          `${v.text}${i < selectedVerses.length - 1 ? ' ' : ''}`
        ).join('');

        const shareText = `${combinedText} - ${shareTitle}`;

        // Show share popup at right-click position with preview
        setSharePopup({
          show: true,
          title: shareTitle,
          text: shareText,
          url: shareUrl,
          x: e.pageX,
          y: e.pageY,
          preview: combinedText // Show preview of selected verses
        });
      }
    };

    const handleMouseOver = (e) => {
      const verseId = findVerseIdFromTarget(e.target);
      if (!supportsCommentary || !verseId) {
        return;
      }

      const marker = document.getElementById(verseId);
      marker?.classList.add('verse-commentary-hover');
      marker?.nextElementSibling?.classList.add('verse-commentary-hover');
    };

    const handleMouseOut = (e) => {
      const verseId = findVerseIdFromTarget(e.target);
      if (!verseId) {
        return;
      }

      const marker = document.getElementById(verseId);
      marker?.classList.remove('verse-commentary-hover');
      marker?.nextElementSibling?.classList.remove('verse-commentary-hover');
    };

    // Add event listener to content div
    const contentDiv = document.querySelector('.content section');
    if (contentDiv) {
      contentDiv.addEventListener('click', handleVerseClick);
      contentDiv.addEventListener('contextmenu', handleContextMenu);
      contentDiv.addEventListener('mouseover', handleMouseOver);
      contentDiv.addEventListener('mouseout', handleMouseOut);
    }

    // Cleanup
    return () => {
      if (contentDiv) {
        contentDiv.removeEventListener('click', handleVerseClick);
        contentDiv.removeEventListener('contextmenu', handleContextMenu);
        contentDiv.removeEventListener('mouseover', handleMouseOver);
        contentDiv.removeEventListener('mouseout', handleMouseOut);
      }
    };
  }, [content, section, bookNamesJa, supportsCommentary, effectiveChapter, commentaryHtml]);

  return (
    <div className={`bibleframe ${supportsCommentary ? (isCommentaryPanelOpen ? 'commentary-open' : 'commentary-collapsed') : 'commentary-none'}`}>
      <Header
        bible={true}
        subon={true}
        title={metaData.title}
        description={metaData.description}
        url={metaData.url}
        image={metaData.image}
      />
      <ChapterNavigation
        htmlContent={content}
        currentChapter={currentChapter}
        onChapterClick={handleChapterSelect}
      />
      <div className="flex-container">
        <div className="left-area">
          <AdIcons />
        </div>

        <div className="content">
          <section>
            {showComingSoon ? (
              <ComingSoon bookName={section} />
            ) : (
              <div dangerouslySetInnerHTML={{ __html: content }} />
            )}
          </section>
          <div className="mobile-ads">
            <AdIcons />
          </div>
        </div>

        <div className={`right-area ${supportsCommentary && !isCommentaryPanelOpen ? 'right-area--collapsed' : ''}`}>
          {supportsCommentary ? (
            <>
              <button
                type="button"
                className="commentary-toggle"
                onClick={() => setIsCommentaryPanelOpen((open) => !open)}
                aria-expanded={isCommentaryPanelOpen}
              >
                {isCommentaryPanelOpen ? '閉じる' : '註解'}
              </button>
              {isCommentaryPanelOpen && (
                <div className="commentary-container">
                  <div className="commentary-header">
                    <div>
                      <div className="commentary-title">マシュー・ヘンリー註解</div>
                      {selectedCommentaryReference && (
                        <div className="commentary-ref">
                          {(bookNamesJa[section] || section)} {selectedCommentaryReference}
                        </div>
                      )}
                    </div>
                    <div className="commentary-nav">
                      <button
                        type="button"
                        onClick={() => handleCommentaryChapterChange(-1)}
                        disabled={!hasPreviousCommentaryChapter}
                      >
                        前
                      </button>
                      <button
                        type="button"
                        onClick={() => handleCommentaryChapterChange(1)}
                        disabled={!hasNextCommentaryChapter}
                      >
                        次
                      </button>
                    </div>
                  </div>
                  <div className="commentary-scroll" ref={commentaryContainerRef}>
                    {isCommentaryLoading ? (
                      <div className="commentary-status">読み込み中...</div>
                    ) : (
                      <div
                        className="commentary-body"
                        dangerouslySetInnerHTML={{ __html: commentaryHtml }}
                      />
                    )}
                  </div>
                </div>
              )}
            </>
          ) : (
            <span className="pad">
              <iframe
                title="bandcamp-player"
                style={{
                  border: 0,
                  width: '100%',
                  height: '80%',
                }}
                src={`https://bandcamp.com/EmbeddedPlayer/album=${album}/size=large/bgcol=333333/linkcol=e99708/transparent=true/`}
                seamless
              >
                <a href="https://asmrchruch.bandcamp.com/album/asmr-32">ASMR Church Bandcamp</a>
              </iframe>
            </span>
          )}
        </div>
      </div>

      {/* Share Popup */}
      {sharePopup.show && (
        <>
          <div
            style={{
              position: 'fixed',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              zIndex: 999
            }}
            onClick={() => setSharePopup({ ...sharePopup, show: false })}
          />
          <div
            style={{
              position: 'absolute',
              left: sharePopup.x,
              top: sharePopup.y,
              backgroundColor: 'white',
              border: '1px solid #ccc',
              borderRadius: '8px',
              padding: '12px',
              boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
              zIndex: 1000,
              maxWidth: '500px'
            }}
          >
            {sharePopup.preview && (
              <div
                style={{
                  backgroundColor: '#f9f9f9',
                  padding: '10px',
                  borderRadius: '4px',
                  marginBottom: '12px',
                  fontSize: '14px',
                  lineHeight: '1.5',
                  maxHeight: '150px',
                  overflowY: 'auto',
                  borderLeft: '3px solid #e99708'
                }}
              >
                <div style={{ fontWeight: 'bold', marginBottom: '6px', color: '#333' }}>
                  {sharePopup.title}
                </div>
                <div style={{ color: '#555' }}>
                  {sharePopup.preview}
                </div>
              </div>
            )}
            <div
              style={{
                display: 'flex',
                gap: '8px',
                alignItems: 'center'
              }}
            >
              <span style={{ fontSize: '14px', marginRight: '8px' }}>布教する:</span>
              <TwitterShareButton url={sharePopup.url} title={sharePopup.text}>
                <XIcon size={32} round={false} />
              </TwitterShareButton>
              <FacebookShareButton url={sharePopup.url} quote={sharePopup.text}>
                <FacebookIcon size={32} round={false} />
              </FacebookShareButton>
              <EmailShareButton url={sharePopup.url} subject={sharePopup.title} body={sharePopup.text}>
                <EmailIcon size={32} round={false} />
              </EmailShareButton>
              <RedditShareButton url={sharePopup.url} title={sharePopup.text}>
                <RedditIcon size={32} round={false} />
              </RedditShareButton>
              <LinkedinShareButton url={sharePopup.url} title={sharePopup.title} summary={sharePopup.text}>
                <LinkedinIcon size={32} round={false} />
              </LinkedinShareButton>
              <BloggerShareButton
                url={sharePopup.url}
                title={sharePopup.title}
                text={sharePopup.preview || sharePopup.text}
                image="https://www.asmrchurch.com/static/images/c4.jpg"
              >
                <BloggerIcon size={32} />
              </BloggerShareButton>
            </div>
          </div>
        </>
      )}

      <Footer />

      {/* Verse highlight styles */}
      <style>{`
        .verse-highlight {
          background-color: rgba(255, 255, 0, 0.3);
          padding: 2px 4px;
          border-radius: 3px;
          transition: background-color 0.3s ease;
        }
      `}</style>
    </div>
  );
}

export default Bible;
